import type {
  CompetitionLink,
  EventLinkWitOpenDate,
  MeetingLink,
  SearchEndpointResponse
} from '@ps/isp-web-services-types';

import { initialSearchResults } from '../model/defaultState';
import type { SearchResults } from '../model/types';

export function getEventsMatchingQuery(
  searchQuery: string,
  eventLinks: SearchEndpointResponse['eventLinks']
) {
  return eventLinks.filter((eventLink) => eventLink.text.toLowerCase().includes(searchQuery));
}

export function getEventsMappedToSearchMatches(
  eventLinks: SearchEndpointResponse['eventLinks'],
  competitionLinks: SearchEndpointResponse['competitionLinks']
): SearchResults['matches'] {
  return eventLinks.map((event) => ({
    alias: 'sportsEvent',
    link: {
      ...event,
      // TODO https://jira.pyrsoftware.ca/jira/browse/GSFET-1827
      competitionName: competitionLinks.find(
        (competitionLink) => competitionLink.competitionId === event.competitionId
      )?.text
    }
  }));
}

export function getRunnersMatchingQuery(
  searchQuery: string,
  runnersLinks: SearchEndpointResponse['runnersLinks']
) {
  return runnersLinks.filter((runnerLink) =>
    runnerLink.runnerName.toLowerCase().includes(searchQuery)
  );
}

export function getRunnersMappedToSearchMatches(
  runnersLinks: SearchEndpointResponse['runnersLinks']
): SearchResults['matches'] {
  return runnersLinks.map((runner) => ({
    alias: 'sportsAnimalRacingRace',
    link: runner
  }));
}

export function getCompetitionsMatchingQuery(
  searchQuery: string,
  competitionLinks: SearchEndpointResponse['competitionLinks']
) {
  return competitionLinks.filter((competitionLink) =>
    competitionLink.text.toLowerCase().includes(searchQuery)
  );
}

export function getCompetitionsMappedToSearchMatches(
  competitionLinks: SearchEndpointResponse['competitionLinks']
): SearchResults['matches'] {
  return competitionLinks.map((competition) => ({
    alias: 'sportsCompetition',
    link: competition
  }));
}

export function getMeetingsMatchingQuery(
  searchQuery: string,
  meetingsLinks: SearchEndpointResponse['meetingsLinks']
) {
  return meetingsLinks.filter((meetingLink) =>
    meetingLink.venue.toLowerCase().includes(searchQuery)
  );
}

export function getMeetingsMappedToSearchMatches(
  meetingsLinks: SearchEndpointResponse['meetingsLinks']
): SearchResults['matches'] {
  return meetingsLinks.map((meeting) => ({
    alias: 'sportsAnimalRacingRace',
    link: meeting
  }));
}

export function getUniqueEventTypes(
  links: (EventLinkWitOpenDate | CompetitionLink | MeetingLink)[],
  eventTypeLinks: SearchEndpointResponse['eventTypeLinks']
) {
  const eventTypeMap = new Map(eventTypeLinks.map((link) => [link.eventTypeId, link]));
  const seenEventTypeIds = new Set();
  const uniqueEventTypes: SearchEndpointResponse['eventTypeLinks'] = [];

  for (const { eventTypeId } of links) {
    if (!seenEventTypeIds.has(eventTypeId)) {
      seenEventTypeIds.add(eventTypeId);
      const eventType = eventTypeMap.get(eventTypeId);
      if (eventType) {
        uniqueEventTypes.push(eventType);
      }
    }
  }
  return uniqueEventTypes;
}

export const getSearchResults = (
  searchQuery: string,
  {
    eventLinks,
    competitionLinks,
    eventTypeLinks,
    meetingsLinks,
    runnersLinks
  }: SearchEndpointResponse
): SearchResults => {
  const eventsByQuery = getEventsMatchingQuery(searchQuery, eventLinks);
  const runnersByQuery = getRunnersMatchingQuery(searchQuery, runnersLinks);

  if (eventsByQuery.length || runnersByQuery.length) {
    const combinedMatches = [...eventsByQuery, ...runnersByQuery];

    const eventsMatches = eventsByQuery.length
      ? getEventsMappedToSearchMatches(eventsByQuery, competitionLinks)
      : [];
    const runnersMatches = runnersByQuery.length
      ? getRunnersMappedToSearchMatches(runnersByQuery)
      : [];

    return {
      uniqueEventTypes: getUniqueEventTypes(combinedMatches, eventTypeLinks),
      matches: [...eventsMatches, ...runnersMatches]
    };
  }

  const competitionsByQuery = getCompetitionsMatchingQuery(searchQuery, competitionLinks);
  const meetingsByQuery = getMeetingsMatchingQuery(searchQuery, meetingsLinks);

  if (competitionsByQuery.length || meetingsByQuery.length) {
    const combinedMatches = [...competitionsByQuery, ...meetingsByQuery];

    const competitionMatches = competitionsByQuery.length
      ? getCompetitionsMappedToSearchMatches(competitionsByQuery)
      : [];
    const meetingMatches = meetingsByQuery.length
      ? getMeetingsMappedToSearchMatches(meetingsByQuery)
      : [];

    return {
      uniqueEventTypes: getUniqueEventTypes(combinedMatches, eventTypeLinks),
      matches: [...competitionMatches, ...meetingMatches]
    };
  }

  return initialSearchResults;
};
